import { type ReactElement, useMemo } from 'react';
import {
  type MetricThresholdEnabledState,
  MetricThresholdsSection,
} from '@/features/projects/components/MetricThresholdsSection';
import { buildThresholdDrafts } from '@/features/projects/metricThresholdDrafts';
import { buildMetricThresholdPayload } from '@/features/projects/utils/buildMetricThresholdPayload';
import {
  defaultProjectMetricThresholds,
  mergeProjectThresholdGroup,
} from '@/features/projects/utils/projectMetricThresholds';
import { projectExecutionRunThresholdsAnchorId } from '@/features/projects/utils/projectSettingsRoute';
import { isMetricThresholdDisabled } from '@/shared/utils/metricsThresholds';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts';
import type { useUpdateProject } from '@/features/projects';
import type { useMetricThresholdDrafts } from '@/features/projects/hooks/useMetricThresholdDrafts';
import type { MetricThresholdDrafts } from '@/features/projects/metricThresholdDrafts';
import type { SpacingToken } from '@/theme/tokens';
import type { MetricThresholdGroup, ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type MetricThresholdKey = 'suites' | 'executionRuns' | 'tests' | 'pipelines' | 'stages' | 'jobs';

type ProjectMetricThresholdSectionProps = {
  title: string;
  description: string;
  successMessage: string;
  thresholdKey: MetricThresholdKey;
  drafts: MetricThresholdDrafts;
  setDrafts: ReturnType<typeof useMetricThresholdDrafts>['setDrafts'];
  preferredUnitsRef: ReturnType<typeof useMetricThresholdDrafts>['preferredUnitsRef'];
  isValid: boolean;
  isPending: boolean;
  gridSpacing: SpacingToken;
  project: ProjectDto | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  resetDrafts: (thresholds: MetricThresholdGroup) => void;
  container?: 'panel' | 'none';
  disabled?: boolean;
  disabledReason?: string;
};
const defaultThresholdGroupByKey: Record<MetricThresholdKey, MetricThresholdGroup> =
  defaultProjectMetricThresholds;

const buildFrequencySubjectFormatter = (
  singularLabel: string,
  pluralLabel: string,
  compactLabel = 'runs',
): ((value: string) => string) => {
  return (value: string): string => {
    const trimmedValue = value.trim();
    const singular = trimmedValue === '1';
    if (compactLabel.length > 0) {
      return singular ? 'run per' : `${compactLabel} per`;
    }
    return singular ? `${singularLabel} per` : `${pluralLabel} per`;
  };
};

const areVisibleThresholdDraftsDirty = (
  drafts: MetricThresholdDrafts,
  thresholds: MetricThresholdGroup,
): boolean => {
  const baselineDrafts = buildThresholdDrafts(thresholds);

  return (
    drafts.passRate.target !== baselineDrafts.passRate.target ||
    drafts.passRate.warn !== baselineDrafts.passRate.warn ||
    drafts.frequency.targetValue !== baselineDrafts.frequency.targetValue ||
    drafts.frequency.targetUnit !== baselineDrafts.frequency.targetUnit ||
    drafts.frequency.warnValue !== baselineDrafts.frequency.warnValue ||
    drafts.frequency.warnUnit !== baselineDrafts.frequency.warnUnit ||
    drafts.flakeScore.target !== baselineDrafts.flakeScore.target ||
    drafts.flakeScore.warn !== baselineDrafts.flakeScore.warn ||
    drafts.avgDuration.targetValue !== baselineDrafts.avgDuration.targetValue ||
    drafts.avgDuration.targetUnit !== baselineDrafts.avgDuration.targetUnit ||
    drafts.avgDuration.warnValue !== baselineDrafts.avgDuration.warnValue ||
    drafts.avgDuration.warnUnit !== baselineDrafts.avgDuration.warnUnit
  );
};

export const ProjectMetricThresholdSection = ({
  title,
  description,
  successMessage,
  thresholdKey,
  drafts,
  setDrafts,
  preferredUnitsRef,
  isValid,
  isPending,
  gridSpacing,
  project,
  updateProject,
  mutationError,
  notify,
  resetDrafts,
  container = 'panel',
  disabled = false,
  disabledReason,
}: ProjectMetricThresholdSectionProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const isCompact = layoutMode === layoutModeTokens.compact;
  const deriveInitialEnabled = useMemo(
    () =>
      (thresholds: MetricThresholdGroup | null | undefined): MetricThresholdEnabledState => ({
        passRate: thresholds ? !isMetricThresholdDisabled(thresholds.passRate) : true,
        frequency: thresholds ? !isMetricThresholdDisabled(thresholds.frequency) : true,
        flakeScore: thresholds ? !isMetricThresholdDisabled(thresholds.flakeScore) : true,
        avgDuration: thresholds ? !isMetricThresholdDisabled(thresholds.avgDuration) : true,
      }),
    [],
  );
  const memoizedThresholds = useMemo(
    () =>
      project
        ? mergeProjectThresholdGroup(
            defaultThresholdGroupByKey[thresholdKey],
            project.metricThresholds[thresholdKey],
          )
        : defaultThresholdGroupByKey[thresholdKey],
    [project, thresholdKey],
  );
  const initialEnabled = useMemo(
    () => deriveInitialEnabled(memoizedThresholds),
    [deriveInitialEnabled, memoizedThresholds],
  );
  const visibleThresholdDraftsDirty = useMemo(
    () => areVisibleThresholdDraftsDirty(drafts, memoizedThresholds),
    [drafts, memoizedThresholds],
  );
  const baselineKey = useMemo(
    () =>
      JSON.stringify({
        thresholds: {
          passRate: memoizedThresholds.passRate,
          frequency: memoizedThresholds.frequency,
          flakeScore: memoizedThresholds.flakeScore,
          avgDuration: memoizedThresholds.avgDuration,
        },
        enabled: initialEnabled,
      }),
    [initialEnabled, memoizedThresholds],
  );

  return (
    <MetricThresholdsSection
      title={title}
      description={description}
      passRateLabel="Success Rate"
      frequencyLabel={(() => {
        if (thresholdKey === 'pipelines') {
          return 'Job Frequency';
        }
        if (thresholdKey === 'stages') {
          return 'Stage Frequency';
        }
        if (thresholdKey === 'jobs') {
          return 'Job Frequency';
        }
        return 'Run Frequency';
      })()}
      frequencySubjectFormatter={(() => {
        if (thresholdKey === 'executionRuns') {
          return isCompact
            ? buildFrequencySubjectFormatter('execution run', 'execution runs', 'runs')
            : buildFrequencySubjectFormatter('execution run', 'execution runs', '');
        }
        if (thresholdKey === 'suites') {
          return isCompact
            ? buildFrequencySubjectFormatter('suite run', 'suite runs', 'runs')
            : buildFrequencySubjectFormatter('suite run', 'suite runs', '');
        }
        if (thresholdKey === 'tests') {
          return isCompact
            ? buildFrequencySubjectFormatter('test run', 'test runs', 'runs')
            : buildFrequencySubjectFormatter('test run', 'test runs', '');
        }
        if (thresholdKey === 'pipelines') {
          return buildFrequencySubjectFormatter('job', 'jobs', '');
        }
        if (thresholdKey === 'stages') {
          return isCompact
            ? buildFrequencySubjectFormatter('stage run', 'stage runs', 'runs')
            : buildFrequencySubjectFormatter('stage run', 'stage runs', '');
        }
        return buildFrequencySubjectFormatter('job', 'jobs', '');
      })()}
      avgDurationContextLabel={(() => {
        if (thresholdKey === 'executionRuns') {
          return isCompact ? 'per run' : 'per execution run';
        }
        if (thresholdKey === 'suites') {
          return isCompact ? 'per run' : 'per suite run';
        }
        if (thresholdKey === 'tests') {
          return isCompact ? 'per run' : 'per test run';
        }
        if (thresholdKey === 'pipelines') {
          return 'per job';
        }
        if (thresholdKey === 'stages') {
          return isCompact ? 'per run' : 'per stage run';
        }
        return 'per job';
      })()}
      drafts={drafts}
      setDrafts={setDrafts}
      preferredUnitsRef={preferredUnitsRef}
      initialEnabled={initialEnabled}
      isDirty={visibleThresholdDraftsDirty}
      isValid={isValid}
      isPending={isPending}
      baselineKey={baselineKey}
      gridSpacing={gridSpacing}
      container={container}
      disabled={disabled}
      disabledReason={disabledReason}
      anchorBaseId={
        thresholdKey === 'executionRuns' ? projectExecutionRunThresholdsAnchorId : undefined
      }
      onCancel={() => {
        if (!project) return;
        resetDrafts(memoizedThresholds);
        mutationError.clearError();
      }}
      onSubmit={(enabled) => {
        if (!project) return;
        const payload = buildMetricThresholdPayload(drafts, enabled, memoizedThresholds);
        updateProject.mutate(
          { id: project.id, metricThresholds: { [thresholdKey]: payload } },
          {
            onSuccess: () => {
              resetDrafts(payload);
              mutationError.clearError();
              notify({ message: successMessage, tone: 'green' });
            },
            onError: (err) => {
              mutationError.setError(err);
            },
          },
        );
      }}
    />
  );
};
